import { ComponentProps, useCallback } from "react";

import Alert from "../components/Alert";
import { overlays } from "../components/OverlayController";
import { useOverlay } from "./useOverlay";
import { useTranslation } from "~/hooks/useTranslation";
import { ENameSpace } from "~/i18n/@types/types";

type AlertProps = ComponentProps<typeof Alert>;
type AlertPropsWithoutText = Omit<AlertProps, "text">;

export const useAlert = () => {
  const { open } = useOverlay();
  const { t } = useTranslation([ENameSpace.Common]);

  const openAlert = useCallback(
    (alertProps: AlertProps) => {
      return new Promise<void>((resolve) => {
        const { onClose, ...restProps } = alertProps;
        open(overlays.alert, {
          ...restProps,
          onClose: () => {
            onClose?.();
            return resolve();
          },
        });
      });
    },
    [open],
  );

  const openAlertWhenSaved = useCallback(
    (alertPropsWithoutText?: AlertPropsWithoutText) =>
      openAlert({
        text: t(`${ENameSpace.Common}:ApiAlert.SavedSuccess`),
        ...alertPropsWithoutText,
      }),
    [openAlert, t],
  );

  const openAlertWhenSomethingWentWrong = useCallback(
    (alertPropsWithoutText?: AlertPropsWithoutText) =>
      openAlert({
        text: t(`${ENameSpace.Common}:ApiAlert.SomethingWentWrong`),
        ...alertPropsWithoutText,
      }),
    [openAlert, t],
  );

  return { openAlert, openAlertWhenSaved, openAlertWhenSomethingWentWrong };
};

/** @reference https://github.com/marpple/ciety-fe/blob/3e3340532d916e04983b791dda7691bd0ec2a45e/src/context/alert.tsx */
